/**
 * Phone layout for the coach lesson editor: what counts as a phone, and the
 * stage sizes of docs/mobile-editor-layout-spec.md.
 *
 * Detection is JavaScript, not a media query. The result is written to
 * <html data-editor-phone>, which the `phone:` Tailwind variant in index.css
 * keys on, so the CSS and the JS can never disagree.
 *
 * A phone is any of:
 *   - (pointer: coarse) and (hover: none)
 *   - navigator.maxTouchPoints > 1 on a Safari (WebKit, not Chromium) UA
 *   - the smaller viewport side under 500, on a touch-capable device. The
 *     touch condition keeps a short desktop browser window on the desktop
 *     layout; every phone has touch points, so it cannot miss one
 *   - an iPhone or iPod UA
 * and never an iPad (an iPad UA, or iPadOS presenting as a Mac with touch
 * points), which keeps the desktop layout.
 */
export interface PhoneDetection {
  phone: boolean;
  /** Which rule decided it, for the editorDebug overlay. */
  reason: string;
  coarseNoHover: boolean;
  webkitTouch: boolean;
  minSide: number;
  touchCapable: boolean;
  iPhoneUA: boolean;
  iPad: boolean;
}

export function detectPhone(): PhoneDetection {
  if (typeof window === 'undefined') {
    return { phone: false, reason: 'no window', coarseNoHover: false, webkitTouch: false, minSide: 0, touchCapable: false, iPhoneUA: false, iPad: false };
  }
  const ua = navigator.userAgent;
  const touchPoints = navigator.maxTouchPoints || 0;
  const coarseNoHover = window.matchMedia('(pointer: coarse) and (hover: none)').matches;
  const safariUA = /AppleWebKit/.test(ua) && !/(Chrome|Chromium|CriOS|FxiOS|EdgiOS|Edg\/|OPR|Android)/.test(ua);
  const webkitTouch = safariUA && touchPoints > 1;
  const minSide = Math.min(window.innerWidth, window.innerHeight);
  const touchCapable = touchPoints > 0 || window.matchMedia('(any-pointer: coarse)').matches;
  const iPhoneUA = /iPhone|iPod/.test(ua);
  const iPad = /iPad/.test(ua) || (/Macintosh/.test(ua) && touchPoints > 1);
  const base = { coarseNoHover, webkitTouch, minSide, touchCapable, iPhoneUA, iPad };
  if (iPad) return { ...base, phone: false, reason: 'iPad' };
  if (iPhoneUA) return { ...base, phone: true, reason: 'iPhone/iPod UA' };
  if (coarseNoHover) return { ...base, phone: true, reason: 'pointer coarse + hover none' };
  if (webkitTouch) return { ...base, phone: true, reason: 'touch points on Safari UA' };
  if (minSide < 500 && touchCapable) return { ...base, phone: true, reason: `smaller side ${minSide} < 500 with touch` };
  return { ...base, phone: false, reason: 'none matched' };
}

/** Writes the decision where the phone: CSS variant reads it. */
export function applyPhoneAttribute(phone: boolean): void {
  if (typeof document === 'undefined') return;
  document.documentElement.toggleAttribute('data-editor-phone', phone);
}

export interface Insets { top: number; right: number; bottom: number; left: number }

/** A snapshot of the viewport, taken when a mode opens or the phone rotates. */
export interface PhoneViewport {
  phone: boolean;
  landscape: boolean;
  /** innerWidth / innerHeight at the snapshot. */
  w: number;
  h: number;
  insets: Insets;
}

export interface Size { w: number; h: number }

/** Header height (spec 10.1): one 44 bar in every mode and orientation. */
export const PHONE_HEADER = 44;
export const HEADER_PORTRAIT = PHONE_HEADER;
export const HEADER_LANDSCAPE = PHONE_HEADER;
/** One transport or tools row, and the transport with its padding. */
export const ROW = 44;
export const TRANSPORT_ONE_ROW = 52;
export const TRANSPORT_TWO_ROWS = 96;
/** The recorder's actions row (a 48 button and its spacing) and panel label row. */
export const ACTIONS_ROW = 56;
export const LABEL_ROW = 32;

/** Width over height before a video has reported its own (spec: 16:9 until metadata). */
export const DEFAULT_ASPECT = 16 / 9;

/** Content width: the viewport less the side insets. */
export const contentWidth = (v: PhoneViewport) => v.w - v.insets.left - v.insets.right;

/** Landscape plan side panel (spec 9.2): at most 30% of the content width, never over 300. */
export function railWidth(v: PhoneViewport): number {
  return Math.min(300, Math.round(0.3 * contentWidth(v)));
}

/** Viewing controls (spec 9.2): portrait, a 32 scrubber row over a 44 icon row; landscape, one 44 strip. */
export const VIEW_SCRUB_ROW = 32;
export const VIEW_ICON_ROW = 44;

/** The largest box of the given aspect (width / height) inside maxW x maxH. */
export function fitAspect(aspect: number, maxW: number, maxH: number): Size {
  const a = aspect > 0 && Number.isFinite(aspect) ? aspect : DEFAULT_ASPECT;
  let w = Math.max(0, Math.floor(maxW));
  let h = Math.round(w / a);
  if (h > maxH) {
    h = Math.max(0, Math.floor(maxH));
    w = Math.round(h * a);
  }
  return { w, h };
}

/**
 * Portrait stages are bound by the width; this keeps a tall (portrait) clip
 * from taking more than half the usable height.
 */
const portraitMaxH = (v: PhoneViewport) => Math.floor(0.5 * (v.h - v.insets.top - v.insets.bottom));

/**
 * Viewing mode, spec 9.2. `aspect` is the video's own, rotation applied.
 * Portrait: stage, 32 scrubber row and 44 icon row pinned; the plan scrolls
 * below. Landscape: the plan is a side panel when open, and the stage takes
 * all the height left above one 44 strip.
 */
export function viewingLayout(v: PhoneViewport, planOpen: boolean, aspect: number = DEFAULT_ASPECT) {
  const wc = contentWidth(v);
  if (!v.landscape) {
    const stage = fitAspect(aspect, wc, portraitMaxH(v));
    return { stage, header: HEADER_PORTRAIT, transport: VIEW_SCRUB_ROW + VIEW_ICON_ROW, column: wc, rail: 0 };
  }
  /* Landscape (spec 13.14): the video as large as fits the visible viewport,
     edge to edge and centred; the controls float over it, and the plan and
     the segment sheet open over it, so nothing beside or under it takes
     space and opening them never resizes it. */
  void planOpen;
  return { stage: fitAspect(aspect, v.w, v.h), header: 0, transport: 0, column: v.w, rail: 0 };
}

/** Landscape side panels over the video (spec 13.14): about 320, never over 45% of the width. */
export function landscapeSheetWidth(v: PhoneViewport): number {
  return Math.min(320, Math.round(0.45 * v.w));
}

/** Recorder, landscape (spec 10.5): each side rail, and the timeline row under a single source. */
export const RECORDER_RAIL = 56;
export const TIMELINE_ROW = 32;

/** Side by side: each panel's margin from the screen edge (spec 9.4). */
export const PANEL_MARGIN = 8;



/**
 * Recorder, portrait side by side: the two panels stack, and between them
 * share the height portraitBounds (recorderLayout.ts) gives two panels. Each panel's stage is its
 * own video at the full width when the pair fits; when it does not, both
 * shrink by the same factor. Splitting the height evenly instead held a 16:9
 * clip a few pixels short of the width beside a wider one that needed less.
 */
export function stackedPanelBounds(
  pair: { maxW: number; maxH: number },
  aspectA: number | null,
  aspectB: number | null,
): [{ maxW: number; maxH: number }, { maxW: number; maxH: number }] {
  const a = aspectA && aspectA > 0 ? aspectA : DEFAULT_ASPECT;
  const b = aspectB && aspectB > 0 ? aspectB : DEFAULT_ASPECT;
  const hA = pair.maxW / a;
  const hB = pair.maxW / b;
  const k = Math.min(1, (2 * pair.maxH) / (hA + hB));
  const cap = (h: number) => (k === 1 ? Math.ceil(h) : Math.floor(h * k));
  return [{ maxW: pair.maxW, maxH: cap(hA) }, { maxW: pair.maxW, maxH: cap(hB) }];
}

/** The recorder's landscape transport row: play, scrubber, times, speed, mute. */
export const LANDSCAPE_TRANSPORT = 56;
/** Below this a side-by-side panel's controls take two rows rather than overlap. */
export const PANEL_ONE_ROW_MIN = 400;
/** A side-by-side panel's control height in landscape. */
export const landscapePanelRow = (panelWidth: number) => (panelWidth >= PANEL_ONE_ROW_MIN ? LANDSCAPE_TRANSPORT : 2 * ROW + 8);

/** Preview, spec 5. */
export function previewStage(v: PhoneViewport, hasPrevNext: boolean, aspect: number = DEFAULT_ASPECT): Size {
  const wc = contentWidth(v);
  if (!v.landscape) return fitAspect(aspect, wc, portraitMaxH(v));
  return fitAspect(aspect, wc, v.h - v.insets.top - v.insets.bottom - HEADER_LANDSCAPE - (hasPrevNext ? 52 : 0));
}

/**
 * Whether a new viewport reading should replace the snapshot (spec 11.4).
 *
 * The stage sizes come from the snapshot, and in portrait a stage must not
 * move while the coach works, so there a new reading is taken only when the
 * phone decision, the orientation or the width changes, or the height
 * shrinks (Safari's toolbar coming back would otherwise overflow the
 * layout); a height that grows is ignored. In landscape the stage is bound
 * by the height, so every change is taken: ignoring a height that grows back
 * (the toolbar hiding again after a scroll or a tab switch) left the video
 * stuck small with empty space below it. For a second after a rotation every
 * reading is taken too, because iOS reports sizes from mid-rotation first.
 */
export function shouldResnapshot(
  prev: Pick<PhoneViewport, 'phone' | 'landscape' | 'w' | 'h'>,
  next: Pick<PhoneViewport, 'phone' | 'landscape' | 'w' | 'h'>,
  justRotated: boolean,
): boolean {
  if (next.phone !== prev.phone || next.landscape !== prev.landscape || next.w !== prev.w) return true;
  if (next.h === prev.h) return false;
  if (justRotated || next.landscape) return true;
  return next.h < prev.h;
}
